import React, { useState } from 'react';
import rentalApi from '../services/api';
import './DeletePage.css';

type EntityType = 'pin' | 'listing' | 'search';

const LABELS: Record<EntityType, string> = {
  pin:     'rent pin',
  listing: 'rental listing',
  search:  'rental search',
};

export const DeletePage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const id    = params.get('id') ?? '';
  const token = params.get('token') ?? '';
  const type  = (params.get('type') ?? '') as EntityType;

  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const isValid = id && token && (type === 'pin' || type === 'listing' || type === 'search');
  const label   = isValid ? LABELS[type] : '';

  const handleDelete = async () => {
    setStatus('loading');
    try {
      if (type === 'pin')     await rentalApi.deleteRentPinByToken(id, token);
      if (type === 'listing') await rentalApi.deleteRentalListing(id, token);
      if (type === 'search')  await rentalApi.deleteRentalSearch(id, token);
      setStatus('done');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to delete. The link may have already been used or expired.');
      setStatus('error');
    }
  };

  if (!isValid) {
    return (
      <div className="del-page">
        <div className="del-card">
          <div className="del-icon del-icon--warn">!</div>
          <h2>Invalid link</h2>
          <p>This deletion link is missing required information. Please check the link and try again.</p>
          <a href="/" className="del-btn del-btn--outline">Go to home</a>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="del-page">
        <div className="del-card">
          <div className="del-icon del-icon--ok">✓</div>
          <h2>Deleted</h2>
          <p>Your {label} has been removed.</p>
          <a href="/" className="del-btn">Back to map</a>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="del-page">
        <div className="del-card">
          <div className="del-icon del-icon--warn">!</div>
          <h2>Could not delete</h2>
          <p>{errorMsg}</p>
          <a href="/" className="del-btn del-btn--outline">Go to home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="del-page">
      <div className="del-card">
        <div className="del-icon del-icon--warn">?</div>
        <h2>Delete {label}?</h2>
        <p>This will permanently remove your {label} from the map. This action cannot be undone.</p>
        <div className="del-actions">
          <a href="/" className="del-btn del-btn--outline">Cancel</a>
          <button
            className="del-btn del-btn--danger"
            onClick={handleDelete}
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Deleting…' : 'Yes, delete it'}
          </button>
        </div>
      </div>
    </div>
  );
};
